// src/v2/components/modules/DTRWithOT/DTRWithOTModule.js
import React, { useState } from "react";
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Hooks
import { useFilterManager } from "../../../hooks/useFilterManager";
import { useDTRWithOT } from "../../../hooks/useDTRWithOT";
import { GeneralMessage, useGeneralMessage } from "../../../../common/APICommon";

// Shared Components
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import DTRWithOTTable from "./components/DTRWithOTTable";
import { LoadingSpinner } from "../../../../common/Loading";
import { FEATURES } from "../../../../config/features";
import DTRWithOTAddEditModal from "./components/DTRWithOTAddEditModal";
import { formatDate } from "../../../../utils/textUtils";
import { ConfirmationModal } from "../../../../common/Modal";

const DTRWithOTModule = ({ user }) => {
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);

  const {
    activeFilterState,
    onFilterChange,
    apiFilterPayload,
    additionalApiFilterPayload,
    applyFilters,
    resetFilters
  } = useFilterManager(FEATURES.DTR_WITH_OT_VIEWING_FILTER || []);

  // The hook will now "watch" these values and fetch when they change.
  const dtrMethods = useDTRWithOT(
    showGeneralMessage,
    apiFilterPayload,
    additionalApiFilterPayload,
    user
  );

  const handleEditClick = (item) => {
    setSelectedLog(item);
    setIsModalOpen(true);
  };

  const handleSaveOverride = async (form) => {
    const res = await dtrMethods.upsertOTOverride(form, selectedLog.otHours);
    if (res.success) {
      setIsModalOpen(false);
    }
  };

  const handleApproveClick = (item) => {
    setPendingApproval(item);
    setIsConfirmOpen(true);
  };

  const confirmApproval = async () => {
    const res = await dtrMethods.approveOT(pendingApproval, user.id);
    if (res.success) {
      setIsConfirmOpen(false);
      setPendingApproval(null);
    }
  };

  return (
    <div className="relative">
      <div className="transaction-header p-4 border-b">
        <div className='sub-title row text-sm'>
          <span className="text-gray-400">{"Transactions > "}</span>
          <span className="border-b border-black text-gray-700">{"DTR With OT Viewing"}</span>
        </div>
        <h3 className="text-xl font-bold mt-2">DTR With OT Viewing</h3>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
          <span className="text-lg font-semibold text-gray-600 block mb-4">Filters</span>

          <DynamicFilterBar
            requestedFilters={FEATURES.DTR_WITH_OT_VIEWING_FILTER || []}
            activeFilterState={activeFilterState}
            onFilterChange={onFilterChange}
            showButtons={false}
          />

          <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
            <Button
              name="GENERATE"
              icon={<RestartAltIcon fontSize="small" />}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 font-bold transition-all"
              onClick={() => {
                // MODIFIED: Just apply filters. 
                // The hook's useEffect will detect the payload change and fetch.
                applyFilters();
              }}
            />
            <Button
              name="CLEAR"
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2.5 transition-all"
              onClick={resetFilters}
            />
          </div>
        </div>

        <SearchBar
          placeholder="Search by name or ID..."
          handleFilter={dtrMethods.handleFilter}
        />

        <DTRWithOTTable
          data={dtrMethods.logs}
          onEdit={handleEditClick}
          onApprove={handleApproveClick}
        />

      </div>

      <DTRWithOTAddEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedItem={selectedLog}
        onSave={handleSaveOverride}
      />

      <ConfirmationModal
        visible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmApproval}
        title="Confirm OT Approval"
        message={
          `Are you sure you want to approve ${pendingApproval?.otHours} hours of OT for ${pendingApproval?.employeePersonalInformationFullName} on ${formatDate(pendingApproval?.date)}?`}
        noteMessage="This will create an approved Overtime Request record immediately."
        confirmText="Approve Now"
        type="success"
        isLoading={dtrMethods.loading}
      />

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={dtrMethods.loading} />
    </div>
  );
};

export default DTRWithOTModule;